import DOMPurify from 'dompurify';

/**
 * Allow-list for rich-text content. Deliberately narrow: this is the single security boundary
 * between authored HTML and what the filler's browser renders.
 *
 * Tiptap emits only semantic tags with no attributes we need, except `img`, whose three
 * attributes are gated per-tag by the hooks below rather than by the (tag-blind) `ALLOWED_ATTR`
 * list. Table cells arrive carrying colspan/rowspan/colwidth and the table carries an inline
 * min-width style; all of them are stripped here. Re-parsing the stripped markup in Tiptap
 * reproduces the same table, so nothing about editing depends on them surviving.
 *
 * Exported so renderRichText builds its element-creation guard from this one list rather than an
 * independently maintained copy.
 */
export const RICH_TEXT_ALLOWED_TAGS: string[] = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'h1',
  'h2',
  'h3',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'img',
];

/** The reference in the stored HTML, e.g. `<img data-form-image="{uuid}" width="480" alt="">`. */
export const RICH_TEXT_IMAGE_REF_ATTR = 'data-form-image';

// Anchored at both ends. An unanchored test is the classic bypass.
const IMAGE_REF = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Leading digit is 1-9, so width="0" is rejected rather than kept. renderRichText reads the width
// with getAttribute, which yields the STRING "0", and "0" is truthy, so its `width ? ... : ...`
// guard would pass a zero straight through and render the image at zero pixels.
const IMAGE_WIDTH = /^[1-9]\d{0,3}$/;

const ALLOWED_ATTR: string[] = [RICH_TEXT_IMAGE_REF_ATTR, 'width', 'alt'];

/**
 * A private DOMPurify instance, not the shared default export. The hooks below are the per-tag
 * attribute gate that DOMPurify's global `ALLOWED_ATTR` cannot express (it is checked by
 * attribute NAME only, with no tag scoping), and hooks are registered per instance, so a private
 * one keeps them off every other `sanitize` call in the app. This is the only DOMPurify consumer
 * in `src/tsx`, so instance isolation here is hygiene, not a fix for a live conflict.
 */
const richTextPurify = DOMPurify(window);

richTextPurify.addHook('uponSanitizeAttribute', (node, data) => {
  // <img> is the only element carrying an attribute we allow. ALLOWED_ATTR is global, so without
  // this every allow-listed tag would accept the image reference and a width.
  if (node.nodeName.toLowerCase() !== 'img') {
    data.keepAttr = false;
    return;
  }
  switch (data.attrName) {
    case RICH_TEXT_IMAGE_REF_ATTR:
      data.keepAttr = IMAGE_REF.test(data.attrValue);
      break;
    case 'width':
      data.keepAttr = IMAGE_WIDTH.test(data.attrValue);
      break;
    case 'alt':
      data.keepAttr = true; // text only; DOMPurify escapes it on write-back
      break;
    default:
      // src is rejected here, unconditionally and on every tag. That is the whole security
      // posture of this feature: an image is addressed by an opaque reference we resolve
      // ourselves, so no stored markup ever carries a URL for a browser to fetch. It makes a
      // hotlinked https src, a javascript: src, a data:text/html src and an onerror handler one
      // case rather than four. ALLOWED_URI_REGEXP is deliberately never used: `width` is not in
      // DOMPurify's URI_SAFE_ATTRIBUTES, so narrowing it would silently drop width="480" too.
      data.keepAttr = false;
  }
});

richTextPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName.toLowerCase() !== 'img') return;
  // An <img> with no valid reference (a pasted hotlink, or a reference that failed the UUID
  // test) is removed outright rather than left as a broken-image placeholder. That is the
  // predictable, layout-safe outcome AC8 asks for. <img> is a void element, so removing it
  // mid-walk drops no children.
  if (!IMAGE_REF.test(node.getAttribute(RICH_TEXT_IMAGE_REF_ATTR) ?? '')) {
    node.remove();
    return;
  }
  // Guarantee the accessible name exists: every component test here runs an axe() audit and
  // axe's image-alt rule fails an <img> with no alt attribute at all.
  if (!node.hasAttribute('alt')) node.setAttribute('alt', '');
});

/** Sanitises authored rich-text HTML to the allow-listed subset. Safe to render via dangerouslySetInnerHTML. */
export function sanitizeRichTextHtml(html: string | null | undefined): string {
  return richTextPurify.sanitize(html ?? '', { ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS, ALLOWED_ATTR });
}

/**
 * Every image reference in `html`, in document order, de-duplicated. Sanitises first, so a
 * reference that would not survive rendering never triggers a fetch.
 */
export function extractFormImageRefs(html: string | null | undefined): string[] {
  const clean = sanitizeRichTextHtml(html);
  const refs: string[] = [];
  const seen = new Set<string>();
  const pattern = /<img\b[^>]*\bdata-form-image="([0-9a-f-]{36})"/gi;
  let match = pattern.exec(clean);
  while (match) {
    const ref = match[1];
    if (!seen.has(ref)) {
      seen.add(ref);
      refs.push(ref);
    }
    match = pattern.exec(clean);
  }
  return refs;
}

/**
 * Elements that are content even with no text inside them. An empty table is a visible grid and
 * an image contributes no text at all, so a text-only emptiness test would make either vanish
 * behind RichTextView's emptyText branch.
 */
const STRUCTURAL_CONTENT = /<(?:table|img)[\s>]/i;

/** True when sanitised content has no visible text or elements (treat as "no content"). */
export function isRichTextEmpty(html: string | null | undefined): boolean {
  const clean = sanitizeRichTextHtml(html);
  if (STRUCTURAL_CONTENT.test(clean)) return false;
  // Strip tags, then non-breaking spaces (entity + literal) and remaining whitespace. An empty editor
  // serialises to e.g. "<p></p>" / "<p><br></p>", which reduces to "" here.
  return clean
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .replace(/\u00A0/g, '')
    .replace(/\s/g, '')
    .trim() === '';
}
